'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdPerson,
    MdClose,
    MdFilterList,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getAdmissions,
    deleteAdmission,
    Admission,
    AdmissionFilters,
    ADMISSION_STATUS_LABELS,
    ADMISSION_STATUS_COLORS,
    ADMISSION_TYPE_LABELS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function AdmissionsPage() {
    const navigate = useNavigate()
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [filteredAdmissions, setFilteredAdmissions] = useState<Admission[]>(
        []
    )
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [admissionToDelete, setAdmissionToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [filters, setFilters] = useState<AdmissionFilters>({})
    const [showFilters, setShowFilters] = useState(false)
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadAdmissions()
    }, [filters, selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = admissions.filter(
                adm =>
                    adm.client?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    adm.admissionNumber
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredAdmissions(filtered)
        } else {
            setFilteredAdmissions(admissions)
        }
    }, [searchTerm, admissions])

    const loadAdmissions = async () => {
        setLoading(true)
        try {
            const response = await getAdmissions(undefined, {
                ...filters,
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setAdmissions(response.result)
                setFilteredAdmissions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar internações')
            }
        } catch (error) {
            toast.error('Erro ao carregar internações')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setAdmissionToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!admissionToDelete) return

        setDeleting(true)
        try {
            const response = await deleteAdmission(admissionToDelete)
            if (response.success) {
                toast.success('Internação excluída com sucesso')
                loadAdmissions()
                setShowDeleteModal(false)
                setAdmissionToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir internação')
            }
        } catch (error) {
            toast.error('Erro ao excluir internação')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando internações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdPerson className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Internações
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {admissions.length}{' '}
                            {admissions.length === 1
                                ? 'internação'
                                : 'internações'}
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/admissions/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Nova Internação</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente ou número..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <button
                        className="btn btn-outline gap-2"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList className="text-lg" />
                        Filtros
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-base-300/50 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Status</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.status || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        status:
                                            (e.target.value as any) ||
                                            undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {Object.entries(ADMISSION_STATUS_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Data Início</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered"
                                value={filters.startDate || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        startDate: e.target.value || undefined,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Data Fim</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered"
                                value={filters.endDate || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        endDate: e.target.value || undefined,
                                    })
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            {filteredAdmissions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdPerson className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma internação encontrada'
                                : 'Nenhuma internação cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece registrando uma nova internação'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() =>
                                    navigate('/hospital/admissions/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Nova internação
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Unidade/Leito</th>
                                <th>Tipo</th>
                                <th>Data</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAdmissions.map(adm => (
                                <tr
                                    key={adm.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/admissions/${adm.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdPerson className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <div className="font-bold">
                                                    {adm.client?.name}
                                                </div>
                                                <div className="text-sm text-base-content/60">
                                                    {adm.admissionNumber}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            <div>{adm.unit?.name}</div>
                                            {adm.bed && (
                                                <div className="text-base-content/60">
                                                    Leito {adm.bed.number}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {
                                                ADMISSION_TYPE_LABELS[
                                                    adm.admissionType
                                                ]
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            {new Date(
                                                adm.admissionDate
                                            ).toLocaleDateString('pt-BR')}
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${ADMISSION_STATUS_COLORS[adm.status]}`}
                                        >
                                            {
                                                ADMISSION_STATUS_LABELS[
                                                    adm.status
                                                ]
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/hospital/admissions/${adm.id}/editar`
                                                    )
                                                }}
                                                className="btn btn-ghost btn-sm"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    handleDelete(adm.id)
                                                }}
                                                className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setAdmissionToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir internação"
                message="Tem certeza de que deseja excluir esta internação? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
